/**
 * Facebook Messenger Platform - Base Webhook Types
 * 
 * This file contains the common base types and interfaces shared across
 * all webhook event types. It provides the foundation for a properly
 * structured type system with inheritance and discriminated unions.
 * 
 * @module webhooks/base-types
 */

/**
 * Common sender interface for all webhook events.
 * Represents the user who initiated the action.
 */
export interface WebhookSender {
  /** 
   * Page-scoped ID (PSID) of the user.
   * This is the unique identifier for the user within the context of your page.
   */
  id: string;
  
  /** 
   * User reference provided by the chat plugin, if applicable.
   * Only present for chat plugin events where the user hasn't been identified yet.
   */
  user_ref?: string;
}

/**
 * Common recipient interface for all webhook events.
 * Represents the Facebook Page that received the event.
 */
export interface WebhookRecipient {
  /** Facebook Page ID that received the event */
  id: string;
}

/**
 * Base interface for all webhook events.
 * Contains the common properties shared by all event types.
 */
export interface BaseWebhookEvent {
  /** Information about the user who initiated the event */
  sender: WebhookSender;
  
  /** Information about the page that received the event */
  recipient: WebhookRecipient;
  
  /** 
   * Unix timestamp when the event occurred (in milliseconds since epoch).
   * Represents when the action was performed, not when the webhook was sent.
   */
  timestamp: number;
}

/**
 * Webhook event types enum with discriminator values.
 * Used for type narrowing in discriminated unions.
 */
export enum WebhookEventType {
  // Messenger Platform Events (entry[].messaging[])
  MESSAGE = 'message',
  MESSAGE_ECHO = 'message_echo',
  MESSAGE_EDIT = 'message_edit',
  MESSAGE_REACTION = 'reaction',
  MESSAGE_READ = 'read',
  MESSAGING_FEEDBACK = 'messaging_feedback',
  MESSAGING_POSTBACK = 'postback',

  // Page Webhook Events (entry[].changes[])
  FEED = 'feed',
  VIDEOS = 'videos',
  LIVE_VIDEOS = 'live_videos',
}

/**
 * Generic webhook entry structure.
 * Represents a single entry in the webhook payload.
 */
export interface WebhookEntry<T extends BaseWebhookEvent = BaseWebhookEvent> {
  /** Unique ID of the page */
  id: string;
  
  /** Time of update (epoch time in milliseconds) */
  time: number;
  
  /** Array of messaging events */
  messaging: T[];
}

/**
 * Generic webhook payload structure.
 * This is the top-level structure received from Facebook webhooks.
 */
export interface WebhookPayload<T extends BaseWebhookEvent = BaseWebhookEvent> {
  /** Always 'page' for Messenger webhooks */
  object: 'page';
  
  /** Array of entry objects containing the actual events */
  entry: WebhookEntry<T>[];
}

/**
 * Type guard to check if an object has the base webhook event structure
 * 
 * @param obj - The object to check
 * @returns True if the object has the base webhook event properties
 */
export function isBaseWebhookEvent(obj: any): obj is BaseWebhookEvent {
  return (
    obj &&
    typeof obj === 'object' &&
    'sender' in obj &&
    'recipient' in obj &&
    'timestamp' in obj &&
    typeof obj.sender === 'object' &&
    typeof obj.recipient === 'object' &&
    typeof obj.timestamp === 'number'
  );
}

/**
 * Type guard to check if a sender has a PSID (is identified)
 * 
 * @param sender - The sender object to check
 * @returns True if sender has a valid PSID
 */
export function hasUserId(sender: WebhookSender): sender is WebhookSender & { id: string } {
  return typeof sender.id === 'string' && sender.id.length > 0;
}

/**
 * Type guard to check if a sender has a user reference (anonymous)
 * 
 * @param sender - The sender object to check
 * @returns True if sender has a user_ref
 */
export function hasUserRef(sender: WebhookSender): sender is WebhookSender & { user_ref: string } {
  return typeof sender.user_ref === 'string' && sender.user_ref.length > 0;
}

/**
 * Generic webhook entry structure for Page webhooks (uses 'changes' instead of 'messaging').
 * Represents a single entry in a Page webhook payload.
 */
export interface PageWebhookEntry<T = any> {
  /** Unique ID of the page */
  id: string;

  /** Time of update (epoch time in milliseconds) */
  time: number;

  /** Array of change events */
  changes: T[];
}

/**
 * Generic webhook payload structure for Page webhooks.
 * This is the top-level structure received from Facebook Page webhooks.
 */
export interface PageWebhookPayload<T = any> {
  /** Always 'page' for Page webhooks */
  object: 'page';

  /** Array of entry objects containing the actual events */
  entry: PageWebhookEntry<T>[];
}

/**
 * Extract all events from a generic webhook payload
 *
 * @param payload - The webhook payload to extract events from
 * @returns Array of webhook events
 */
export function extractEvents<T extends BaseWebhookEvent>(
  payload: WebhookPayload<T>
): T[] {
  const events: T[] = [];

  if (payload.object === 'page' && Array.isArray(payload.entry)) {
    for (const entry of payload.entry) {
      if (Array.isArray(entry.messaging)) {
        events.push(...entry.messaging);
      }
    }
  }

  return events;
}

/**
 * Extract all events from a Page webhook payload (uses 'changes' array)
 *
 * @param payload - The Page webhook payload to extract events from
 * @returns Array of Page webhook events
 */
export function extractPageEvents<T>(payload: PageWebhookPayload<T>): T[] {
  const events: T[] = [];

  if (payload.object === 'page' && Array.isArray(payload.entry)) {
    for (const entry of payload.entry) {
      if (Array.isArray(entry.changes)) {
        events.push(...entry.changes);
      }
    }
  }

  return events;
}

/**
 * Common processing context for all webhook events
 */
export interface BaseProcessingContext {
  /** The user who initiated the event (PSID or user_ref) */
  senderId?: string;
  
  /** User reference for anonymous users */
  userRef?: string;
  
  /** The page that received the event */
  recipientId: string;
  
  /** When the event occurred */
  timestamp: number;
  
  /** Whether the sender is identified (has PSID) */
  isIdentifiedUser: boolean;
  
  /** Human-readable datetime for the event timestamp */
  eventDate: Date;
}

/**
 * Extract base processing context from any webhook event
 *
 * @param event - The webhook event to extract context from
 * @returns Base processing context
 */
export function extractBaseContext(event: BaseWebhookEvent): BaseProcessingContext {
  const isIdentifiedUser = hasUserId(event.sender);

  return {
    senderId: event.sender.id,
    userRef: event.sender.user_ref,
    recipientId: event.recipient.id,
    timestamp: event.timestamp,
    isIdentifiedUser,
    eventDate: new Date(event.timestamp),
  };
}

/**
 * Get event types from a Page webhook payload (uses 'changes' array)
 *
 * @param payload - The Page webhook payload to extract event types from
 * @returns Array of unique WebhookEventType values found in the payload
 *
 * @example
 * ```typescript
 * const eventTypes = getPageWebhookEventTypes(payload);
 * console.log('Page events:', eventTypes); // [WebhookEventType.FEED, WebhookEventType.VIDEOS]
 *
 * if (eventTypes.includes(WebhookEventType.FEED)) {
 *   // Process feed events
 * }
 * ```
 */
export function getPageWebhookEventTypes(payload: PageWebhookPayload): WebhookEventType[] {
  const eventTypes = new Set<WebhookEventType>();

  if (payload.object === 'page' && Array.isArray(payload.entry)) {
    for (const entry of payload.entry) {
      if (Array.isArray(entry.changes)) {
        for (const change of entry.changes) {
          if (change && typeof change === 'object' && 'field' in change) {
            const field = change.field as string;
            // Map field value to WebhookEventType enum
            switch (field) {
              case 'feed':
                eventTypes.add(WebhookEventType.FEED);
                break;
              case 'videos':
                eventTypes.add(WebhookEventType.VIDEOS);
                break;
              case 'live_videos':
                eventTypes.add(WebhookEventType.LIVE_VIDEOS);
                break;
            }
          }
        }
      }
    }
  }

  return Array.from(eventTypes);
}